<template>
  <!-- 공지사항 팝업 -->
  <div v-if="showAboutVideoModalCookie && notices.length > 0" id="modal" class="notice-wrap">
    <div class="notice-inner">
      <div class="notice-header">
        <div class="notice-header-title">
          {{ $t('알립니다') }}
        </div>
        <button class="bn-layerClose" @click="closeLayerModal">
          <span class="blind">{{ $t('닫기') }}</span>
        </button>
      </div>

      <div class="notice">
        <swiper
          class="notice-swiper"
          :modules="[Pagination, Autoplay]"
          :slides-per-view="1"
          :space-between="0"
          :pagination="notices.length > 1 ? { clickable: true } : false"
          :autoplay="notices.length > 1 ? { delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true } : false"
          :allow-touch-move="notices.length > 1"
          :loop="notices.length > 1"
        >
          <swiper-slide v-for="notice in notices" :key="notice.id">
            <div class="notice-item">
              <div class="notice-contents">
                <div class="thum-icon" :style="{ backgroundImage: `url('${notice.imgUrl || defaultImage}')` }" />
                <h3 v-if="notice.categoryTransKey">{{ $t(notice.categoryTransKey) }}</h3>
                <h2 class="title" v-html="notice.title" />
                <p v-html="notice.contents"></p>
              </div>
              <div v-if="notice.linkUrl" class="notice-btn">
                <NuxtLink :to="toNoticeLink(notice.linkUrl)" class="btn-link">
                  {{ $t('바로가기') }}
                </NuxtLink>
              </div>
            </div>
          </swiper-slide>
        </swiper>
      </div>

      <form name="layerForm" class="form">
        <label class="layerCheck" @click="closeLayerDuringWeek">
          <input id="todayCheck1" type="checkbox" name="chkbox"><span class="txt">{{ $t('일주일간보지않기') }}</span>
        </label>
      </form>
    </div>

    <div v-if="showAboutVideoModalCookie" class="notice-cover" />
  </div>
</template>

<script lang="ts" setup>
// ---------- swiper
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/pagination";
import "swiper/css/navigation";

interface NoticePopup {
  id: number
  imgUrl: string | null
  popupCategoryGb: number | null
  categoryTransKey: string | null
  title: string
  contents: string
  linkUrl: string | null
}

// 팝업 이미지가 없을 때 사용하는 기본 이미지
const defaultImage = '/images/notice_popup.jpg'

const { locale } = useI18n()
const localePath = useLocalePath()

const { data: popupData } = await useFetch<NoticePopup[]>('/api/popups', {
  method: 'GET',
  query: { languageGbSn: locale.value },
})
const notices = computed(() => popupData.value ?? [])

// 내부 경로는 언어 prefix 를 붙이고, 외부 주소는 그대로 사용
function toNoticeLink(url: string) {
  return url.startsWith('/') ? localePath(url) : url
}

const now = new Date()
const expires = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7)
const showAboutVideoModalCookie = useCookie<boolean>('showAboutVideoModal', { expires, default: () => true })

function closeLayerModal() {
  const modal = document.getElementById('modal')
  modal?.setAttribute('style', 'display:none')
}

function closeLayerDuringWeek() {
  closeLayerModal()
  showAboutVideoModalCookie.value = false
}

onMounted(() => {
  if (!showAboutVideoModalCookie.value)
    return
  // 0.5초 후 팝업 열기
  setTimeout(() => {
    const noticeWrap = document.querySelector('.notice-wrap')
    if (noticeWrap) {
      noticeWrap.classList.add('notice-open')
      if (noticeWrap.classList.contains('notice-hide')) {
        noticeWrap.classList.remove('notice-hide')
      }
    }
  }, 500)

  // 110초 후 팝업 닫기
  setTimeout(() => {
    const noticeWrap = document.querySelector('.notice-wrap')
    if (noticeWrap) {
      noticeWrap.classList.remove('notice-open')
      noticeWrap.classList.add('notice-hide')
    }
  }, 110000)

  // 119초 후 팝업 display:none 처리
  setTimeout(() => {
    const noticeWrap = document.querySelector('.notice-wrap')
    if (noticeWrap && noticeWrap.classList.contains('notice-hide')) {
      noticeWrap.classList.add('notice-none')
    }
  }, 119000)
})
</script>

<style scoped>
/* 공지사항 팝업 */
.notice-wrap {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 201;
  overflow: hidden;
}
.notice-wrap {
  position: fixed;
  bottom: 0;
  overflow: visible;
}
.notice-wrap.notice-none {
  display: none !important;
}
.notice {
  max-width: 1070px;
  margin-left: auto;
  margin-right: auto;
}
.notice-swiper {
  overflow: hidden;
}
.notice-swiper :deep(.swiper-pagination) {
  position: relative !important;
  top: initial !important;
  bottom: initial !important;
  padding-top: 3px;
}
.notice-swiper :deep(.swiper-pagination-bullet) {
  width: 6px !important;
  height: 6px !important;
  border-radius: 10px !important;
  margin: 2px !important;
  background-color: #000 !important;
  opacity: 0.25;
  transition: all 0.25s ease-in-out;
}
.notice-swiper :deep(.swiper-pagination-bullet-active) {
  width: 18px !important;
  background-color: #000 !important;
  opacity: 0.6 !important;
}
.notice-header {
  display: flex;
  justify-content: space-between;
}
.notice-header,
.notice-inner .notice-item {
  padding-left: 20px;
  padding-right: 20px;
}
.notice-header-title {
  display: flex;
  align-items: center;
  font-weight: 600;
}
.notice-header-title::before {
  content: '';
  width: 28px;
  height: 28px;
  display: inline-block;
  margin-right: 2px;
  background-image: url('/images/notice_ico_bell.svg');
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
}
.notice-header .bn-layerClose {
  width: 27px;
  height: 27px;
  border: none;
  background-color: transparent;
  background-image: url('/images/notice_ico_close.svg');
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
}
.notice-inner {
  position: relative;
  z-index: 11;
  max-width: 421px;
  margin-left: auto;
  margin-right: auto;
  padding-top: 20px;
  padding-bottom: 25px;
  border-radius: 20px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  background-color: #fff;
  text-align: center;
}
.notice-inner .notice-contents {
  margin-top: 10px;
  margin-bottom: 20px;
}
.notice-inner .notice-contents > * {
  text-align: center;
}
.notice-inner .notice-contents > *:not(.thum-icon) {
  margin-top: 8px;
}
.notice-inner .thum-icon {
  height: 106px;
  margin-top: -10px;
  margin-bottom: 10px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}
.notice-inner h3 {
  font-size: 15px;
  color:var(--color-brown)
}
.notice-inner h2 {
  font-size: 20px;
  color: #0e0f11;
  letter-spacing: -0.4px;
  font-weight: 600;
  line-height: 1.4;
}
.notice-inner h2,
.notice-inner p {
  white-space: pre-line;
}
.notice-inner p {
  font-size: 14px;
  color: #7b8187;
  line-height: 1.5;
}
.notice-inner .btn-link {
  font-size: 18px;
  color: #fff;
  background-color: var(--color-primary);
  border-radius: 8px;
  padding: 14px 10px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.notice-inner .btn-link::after {
  content: '';
  width: 28px;
  height: 14px;
  margin-left: 10px;
  display: inline-block;
  background-image: url('/images/notice_ico_arrow.svg');
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
}
.notice-inner .form {
  font-size: 15px;
  color: #81878e;
  text-decoration: underline;
  padding: 18px 10px;
}
.notice-inner .form input {
  display: none;
}
.notice-cover {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: #000;
  opacity: 0;
  z-index: 10;
  transition: all 0.3s ease-in-out;
}
/* 애니-기본 */
.notice-wrap .notice-inner {
  -webkit-transform: translateY(100%);
  transform: translateY(100%);
  -webkit-transition: background-color 1s ease-in-out 1.8s, color 1s ease-in-out 1.8s,
    -webkit-transform 0.5s ease-in-out;
  transition: -webkit-transform 0.5s ease-in-out;
  transition: transform 0.5s ease-in-out;
}
/* 애니-열기 */
.notice-open .notice-inner {
  transform: translateY(0);
  -webkit-transform: translateY(0);
}
.notice-open .notice-cover {
  opacity: 0.4;
}
/* 애니-닫기 */
.notice-hide .notice-inner {
  transform: translateY(100%);
  -webkit-transform: translateY(100%);
}
.notice-hide .notice-cover {
  opacity: 0;
}
@media (min-width: 421px) {
  .notice-swiper :deep(.swiper-pagination) {
    padding-top: 8px;
  }
  .notice-swiper :deep(.swiper-pagination-bullet) {
    width: 8px !important;
    height: 8px !important;
    margin: 2.5px !important;
  }
  .notice-swiper :deep(.swiper-pagination-bullet-active) {
    width: 24px !important;
  }
  /* 애니-기본 */
  .notice-wrap .notice-inner {
    transform: translateY(calc(100% + 30px));
    -webkit-transform: translateY(calc(100% + 30px));
  }
  /* 애니-열기 */
  .notice-open .notice-inner {
    transform: translateY(0);
    -webkit-transform: translateY(0);
  }
  /* 애니-닫기 */
  .notice-hide .notice-inner {
    transform: translateY(calc(100% + 30px));
    -webkit-transform: translateY(calc(100% + 30px));
  }
  .notice-inner {
    border-radius: 20px;
    margin-bottom: 30px;
  }
}
@media (min-width: 1070px) {
  .notice-wrap {z-index:199}
  /* 애니-기본 */
  .notice-wrap .notice-inner {
    transform: translateY(calc(-100% - 170px));
    -webkit-transform: translateY(calc(-100% - 170px));
    -webkit-transition: -webkit-transform 0.8s ease-in-out;
    transition: -webkit-transform 0.8s ease-in-out;
    transition: transform 0.8s ease-in-out;
  }
  /* 애니-열기 */
  .notice-open .notice-inner {
    transform: translateY(0);
    -webkit-transform: translateY(0);
  }
  /* 애니-닫기 */
  .notice-hide .notice-inner {
    transform: translateY(calc(-100% - 170px));
    -webkit-transform: translateY(calc(-100% - 170px));
  }
  .notice-wrap {
    max-width: 1260px;
    position: absolute;
    bottom: inherit;
    margin-left: auto;
    margin-right: auto;
    top: 100px;
  }
  .notice-inner {
    max-width: 400px;
    margin-right: 20px;
    margin-top: 20px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }
  .notice-inner .btn-link {
    font-size: 16px;
  }
  .notice-cover {
    display: none;
  }
  .notice-inner h2 {
    font-size: 24px;
    line-height: 1.4;
  }
  .notice-inner .notice-contents > *:not(.thum-icon) {
    margin-top: 8px;
  }
}
</style>
